import { Injectable } from '@angular/core';
import {
  cancelCall,
  disableBackdrop,
  enableBackdrop,
  finalizeCall,
  selectConnectParams,
} from '@epicuro-next/state/connect-state';
import { ComponentStore } from '@ngrx/component-store';
import { select, Store } from '@ngrx/store';
import { EMPTY, fromEvent, Observable } from 'rxjs';
import { catchError, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  createLocalAudioTrack,
  createLocalVideoTrack,
  LocalParticipant,
  LocalVideoTrack,
  LocalVideoTrackPublication,
  Participant,
  RemoteAudioTrackPublication,
  RemoteParticipant,
  RemoteVideoTrackPublication,
  Room,
} from 'twilio-video';

import { ConnectPermissionsService } from './connect-permissions.service';
import { ConnectRoomService, RoomEvents } from './connect-room.service';

export interface ParticipantMediaStatus {
  cameraEnabled?: boolean;
  microphoneEnabled?: boolean;
}

export interface ScreenShareData {
  screenShareTrack?: LocalVideoTrack;
  screenShareStream?: MediaStream;
}

export interface ConnectVideoState {
  localParticipant?: LocalParticipant;
  localParticipantMediaStatus?: ParticipantMediaStatus;
  remoteParticipant?: Participant;
  remoteParticipantMediaStatus?: ParticipantMediaStatus;
  mediaAccessDenied?: boolean;
  screenShareTrack?: LocalVideoTrack;
  screenShareStream?: MediaStream;
  activeRoom?: Room;
  isScreenSharing?: boolean;
  isPictureInPicture?: boolean;
  participantConnected: boolean;
  connectionDone: boolean;
}

const initialState: ConnectVideoState = {
  localParticipantMediaStatus: {
    cameraEnabled: true,
    microphoneEnabled: true,
  },
  remoteParticipantMediaStatus: {
    cameraEnabled: true,
    microphoneEnabled: true,
  },
  isScreenSharing: false,
  isPictureInPicture: false,
  participantConnected: false,
  connectionDone: false,
};

export enum TwilioDeviceKind {
  VIDEO = 'video',
  AUDIO = 'audio',
}

export type ConnectTrackEvent = [
  RemoteAudioTrackPublication | RemoteVideoTrackPublication,
  Participant,
];

@Injectable()
export class ConnectVideoStore extends ComponentStore<ConnectVideoState> {
  constructor(
    private connectPermissionsService: ConnectPermissionsService,
    private connectRoomService: ConnectRoomService,
    private store: Store,
  ) {
    super(initialState);
  }

  public readonly selectConnectVideoState = this.select((state) => state);

  public readonly selectLocalParticipant = this.select(
    (state) => state.localParticipant,
  );
  public readonly selectLocalParticipantMediaStatus = this.select(
    (state) => state.localParticipantMediaStatus,
  );

  public readonly selectMediaAccessDenied = this.select(
    (state) => state.mediaAccessDenied,
  );

  public readonly selectRemoteParticipant = this.select(
    (state) => state.remoteParticipant,
  );

  public readonly selectRemoteParticipantMediaStatus = this.select(
    (state) => state.remoteParticipantMediaStatus,
  );

  public readonly selectIsScreenSharing = this.select(
    (state) => state.isScreenSharing,
  );

  public readonly selectIsPictureInPicture = this.select(
    (state) => state.isPictureInPicture,
  );

  public readonly selectIsConnectionDone = this.select(
    (state) => state.connectionDone,
  );

  public readonly screenTrack = this.select((state) => state.isScreenSharing);

  public readonly checkMediaPermissions = this.effect(
    (void$: Observable<void>) =>
      void$.pipe(
        switchMap(() =>
          this.connectPermissionsService.ensureMediaPermissions(),
        ),
        tap(() => {
          this.joinToRoom();
        }),
        catchError(() => {
          this.setMediaAccesDenied();
          return EMPTY;
        }),
      ),
  );

  public readonly disconnectRoom = this.effect((void$: Observable<void>) =>
    void$.pipe(
      withLatestFrom(this.selectConnectVideoState),
      tap(([, { activeRoom, participantConnected }]) => {
        this.store.dispatch(finalizeCall());
        activeRoom?.localParticipant.videoTracks.forEach(
          (publicationTrack: LocalVideoTrackPublication) =>
            publicationTrack.track.stop(),
        );
        if (!participantConnected) {
          this.store.dispatch(cancelCall({ roomId: activeRoom?.sid }));
        }
        activeRoom?.disconnect();
        this.resetStore();
      }),
    ),
  );

  public readonly toggleMedia = this.effect((mediaKind$: Observable<string>) =>
    mediaKind$.pipe(
      withLatestFrom(this.selectConnectVideoState),
      tap(([kind, state]) => {
        kind === TwilioDeviceKind.VIDEO
          ? this.toggleCamera(state)
          : this.toggleMicrophone(state);
      }),
    ),
  );

  public readonly toggleCamera = this.effect(
    (state$: Observable<ConnectVideoState>) =>
      state$.pipe(
        tap((state: ConnectVideoState) => {
          if (state.localParticipantMediaStatus?.cameraEnabled) {
            state.localParticipant?.videoTracks.forEach((publication) => {
              publication.track.disable();
            });
          } else {
            state.localParticipant?.videoTracks.forEach((publication) => {
              publication.track.enable();
            });
          }
          this.setLocalParticipantMediaStatus({
            cameraEnabled: !state.localParticipantMediaStatus?.cameraEnabled,
          });
        }),
      ),
  );

  public readonly toggleMicrophone = this.effect(
    (state$: Observable<ConnectVideoState>) =>
      state$.pipe(
        tap((state: ConnectVideoState) => {
          if (state.localParticipantMediaStatus?.microphoneEnabled) {
            state.localParticipant?.audioTracks.forEach((publication) => {
              publication.track.disable();
            });
          } else {
            state.localParticipant?.audioTracks.forEach((publication) => {
              publication.track.enable();
            });
          }
          this.setLocalParticipantMediaStatus({
            microphoneEnabled: !state.localParticipantMediaStatus
              ?.microphoneEnabled,
          });
        }),
      ),
  );

  public readonly togglePictureInPicture = this.effect(
    (isPictureInPicture$: Observable<boolean>) =>
      isPictureInPicture$.pipe(
        tap((isPictureInPicture: boolean) => {
          if (isPictureInPicture) {
            this.store.dispatch(enableBackdrop());
          } else {
            this.store.dispatch(disableBackdrop());
          }
          this.setIsPictureInPicture(!isPictureInPicture);
        }),
      ),
  );

  public readonly updateVideoDevice = this.effect(
    (deviceId$: Observable<string>) =>
      deviceId$.pipe(
        withLatestFrom(this.selectConnectVideoState),
        tap(([deviceId, state]) => {
          this.createLocalParticipantVideo(state.localParticipant, deviceId);
        }),
      ),
  );

  public readonly updateAudioDevice = this.effect(
    (deviceId$: Observable<string>) =>
      deviceId$.pipe(
        withLatestFrom(this.selectConnectVideoState),
        tap(([deviceId, state]) => {
          this.createLocalParticipantAudio(state.localParticipant, deviceId);
        }),
      ),
  );

  private readonly joinToRoom = this.effect((void$: Observable<void>) =>
    void$.pipe(
      withLatestFrom(this.store.pipe(select(selectConnectParams))),
      switchMap(([, { token, room }]) =>
        this.connectRoomService.joinRoom(token as string, room?.name as string),
      ),
      tap((room: Room) => {
        this.setRoom(room);
        this.onParticipantConnected(room);
        this.onParticipantDisconnected(room);
        this.onTrackEnabled(room);
        this.onTrackDisabled(room);
        if (room.participants.size > 0) {
          const participantPairValue = room.participants.entries().next().value;
          this.setRemoteParticipant(participantPairValue[1]);
        }
      }),
    ),
  );

  private readonly onParticipantConnected = this.effect(
    (room$: Observable<Room>) =>
      room$.pipe(
        switchMap((room) =>
          fromEvent<RemoteParticipant>(
            room,
            RoomEvents.PARTICIPANT_CONNECTED,
          ).pipe(
            tap((remoteParticipant) => {
              this.setRemoteParticipant(remoteParticipant);
            }),
          ),
        ),
      ),
  );

  private readonly onTrackEnabled = this.effect((room$: Observable<Room>) =>
    room$.pipe(
      switchMap((room) =>
        fromEvent<ConnectTrackEvent>(room, RoomEvents.TRACK_ENABLED).pipe(
          tap(([publication]) => {
            if (publication.kind === TwilioDeviceKind.VIDEO) {
              this.setRemoteParticipantMediaStatus({
                cameraEnabled: true,
              });
            } else {
              this.setRemoteParticipantMediaStatus({
                microphoneEnabled: true,
              });
            }
          }),
        ),
      ),
    ),
  );

  private readonly onTrackDisabled = this.effect((room$: Observable<Room>) =>
    room$.pipe(
      switchMap((room) =>
        fromEvent<ConnectTrackEvent>(room, RoomEvents.TRACK_DISABLED).pipe(
          tap(([publication]) => {
            if (publication.kind === TwilioDeviceKind.VIDEO) {
              this.setRemoteParticipantMediaStatus({
                cameraEnabled: false,
              });
            } else {
              this.setRemoteParticipantMediaStatus({
                microphoneEnabled: false,
              });
            }
          }),
        ),
      ),
    ),
  );

  private readonly onParticipantDisconnected = this.effect(
    (room$: Observable<Room>) =>
      room$.pipe(
        switchMap((room) =>
          fromEvent<RemoteParticipant>(
            room,
            RoomEvents.PARTICIPANT_DISCONNECTED,
          ).pipe(tap(() => this.setRemoteParticipant(undefined))),
        ),
      ),
  );

  public readonly toggleScreenShare = this.effect((void$: Observable<void>) =>
    void$.pipe(
      withLatestFrom(this.selectConnectVideoState),
      tap(([, { screenShareTrack }]) => {
        if (screenShareTrack) {
          this.stopScreenShare();
        } else {
          this.startScreenShare();
        }
      }),
    ),
  );

  private readonly startScreenShare = this.effect((void$: Observable<void>) =>
    void$.pipe(
      switchMap(() =>
        this.connectPermissionsService.acquireDisplayMediaTrack().pipe(
          withLatestFrom(this.selectConnectVideoState),
          tap(([stream, state]) => {
            this.store.dispatch(disableBackdrop());
            const screenTrack = new LocalVideoTrack(stream.getTracks()[0], {
              logLevel: 'off',
              name: `ScreenShare.${Date.now()}`,
            });
            state.activeRoom?.localParticipant.publishTrack(screenTrack);
            this.onScreenShareStopped(screenTrack);
            this.setScreenShareData({
              screenShareTrack: screenTrack,
              screenShareStream: stream,
            });
          }),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  private readonly stopScreenShare = this.effect((void$: Observable<void>) =>
    void$.pipe(
      withLatestFrom(this.selectConnectVideoState),
      tap(([, state]) => {
        if (state.screenShareTrack) {
          this.store.dispatch(enableBackdrop());
          state.activeRoom?.localParticipant.unpublishTrack(
            state.screenShareTrack,
          );
          state.screenShareTrack?.stop();
          state.screenShareStream
            .getTracks()
            .forEach((track: MediaStreamTrack) => track.stop());
        }
      }),
    ),
  );

  private readonly onScreenShareStopped = this.effect(
    (screenShareTrack$: Observable<LocalVideoTrack>) =>
      screenShareTrack$.pipe(
        switchMap((screenShareTrack) =>
          fromEvent<LocalVideoTrack>(
            screenShareTrack,
            RoomEvents.SCREEN_SHARE_STOPPED,
          ).pipe(
            tap(() => {
              this.setScreenShareData({
                screenShareTrack: undefined,
                screenShareStream: undefined,
              });
            }),
          ),
        ),
      ),
  );

  public readonly setLocalParticipantMediaStatus = this.updater(
    (state, mediaStatus: ParticipantMediaStatus) => ({
      ...state,
      localParticipantMediaStatus: {
        ...state.localParticipantMediaStatus,
        ...mediaStatus,
      },
    }),
  );

  public readonly setRemoteParticipantMediaStatus = this.updater(
    (state, mediaStatus: ParticipantMediaStatus) => ({
      ...state,
      remoteParticipantMediaStatus: {
        ...state.remoteParticipantMediaStatus,
        ...mediaStatus,
      },
    }),
  );

  private readonly setIsPictureInPicture = this.updater(
    (state, isPictureInPicture: boolean) => ({
      ...state,
      isPictureInPicture,
    }),
  );

  private readonly setMediaAccesDenied = this.updater((state) => ({
    ...state,
    mediaAccessDenied: true,
    isCameraOn: false,
    isMicrophoneOn: false,
  }));

  private readonly setRoom = this.updater((state, activeRoom: Room) => ({
    ...state,
    activeRoom,
    localParticipant: activeRoom.localParticipant,
    localVideoTrack: Array.from(
      activeRoom.localParticipant.videoTracks.values(),
    )[0].track,
  }));

  private readonly setRemoteParticipant = this.updater(
    (state, remoteParticipant: RemoteParticipant | undefined) => ({
      ...state,
      remoteParticipant,
      remoteParticipantMediaStatus: {
        microphoneEnabled: true,
        cameraEnabled: true,
      },
      participantConnected: true,
      connectionDone: true,
    }),
  );

  private readonly setScreenShareData = this.updater(
    (state, value: ScreenShareData) => ({
      ...state,
      screenShareTrack: value.screenShareTrack,
      screenShareStream: value.screenShareStream,
      isScreenSharing: !!value.screenShareTrack,
    }),
  );

  private readonly resetStore = this.updater(() => initialState);

  public async createLocalParticipantVideo(
    localParticipant: LocalParticipant,
    deviceId?: string,
  ) {
    const publicationTracks = Array.from(localParticipant.videoTracks.values());
    publicationTracks.forEach((publicationTrack) => {
      localParticipant.unpublishTrack(publicationTrack.track);
    });
    const localVideoTrack = await createLocalVideoTrack(
      deviceId ? { deviceId: { exact: deviceId } } : undefined,
    );
    localParticipant.publishTrack(localVideoTrack);
  }

  public async createLocalParticipantAudio(
    localParticipant: LocalParticipant,
    deviceId?: string,
  ) {
    const publicationTracks = Array.from(localParticipant.audioTracks.values());
    publicationTracks.forEach((publicationTrack) => {
      localParticipant.unpublishTrack(publicationTrack.track);
    });
    const localAudioTrack = await createLocalAudioTrack(
      deviceId ? { deviceId: { exact: deviceId } } : undefined,
    );
    localParticipant.publishTrack(localAudioTrack);
  }
}
